import { lazy, startTransition, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DetailHeroCard from '../components/DetailHeroCard';
import FemaRegionPrototypeSection from '../components/FemaRegionPrototypeSection';
import FemaNriRegionMapSection from '../components/FemaNriRegionMapSection';
import { ZillowTabbedTimelineDemo } from '../components/GrossYieldTimelineSection';
import LandlordRegulationSnapshotSection from '../components/LandlordRegulationSnapshotSection';
import { LaborTabbedChartDemo } from '../components/LaborMarketSnapshotSection';
import IndustryTrendPrototype from '../components/IndustryTrendPrototype';
import { HtmlLocationPinMarker } from '../components/MapLocationPin';
import PageLoader from '../components/PageLoader';
import ResearchMarketSwitcherSection from '../components/ResearchMarketSwitcherSection';
import StateCountyLocatorMap from '../components/StateCountyLocatorMap';
import {
  DEFAULT_RESEARCH_MARKET_SORT,
  findResearchMarketBySlug,
  formatResearchMarketRulePercent,
  getResearchMarketLatestRuleRatio,
  sortResearchMarkets,
} from '../lib/researchMarkets';
import { formatDisplayDate } from '../lib/dateFormatting';
import { apiJson, isUnauthorizedError } from '../services/api';
import './Research.css';
import './ResearchMarketDetail.css';

const LscEvictionTrackerMapSection = lazy(() => import('../components/LscEvictionTrackerMapSection'));

const PLACEHOLDER_SECTIONS = [
  'Cashflow Snapshot',
  'BRRRR Deal Math',
  'Flood / Insurance Risk',
  'Schools',
  'Crime',
  'Local Contacts',
  'Notes',
  'Research Assumptions vs Actual Outcome',
];

const STATIC_MAP_DEFAULT_ZOOM = 8;
const STATIC_MAP_MIN_ZOOM = 4;
const STATIC_MAP_MAX_ZOOM = 12;
const STATIC_MAP_MAX_LATITUDE = 85.0511;
const STATIC_MAP_TILE_SIZE = 256;
const STATIC_MAP_GRID_SIZE = 3;
const STATIC_MAP_MAX_COLUMNS = 7;
const STATIC_MAP_MAX_ROWS = 5;

function formatMonth(value) {
  return formatDisplayDate(value, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatHeroCurrency(value) {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function formatHeroInteger(value) {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(Number(value));
}

export function buildMarketHeroMetaItems(market) {
  const populationYear = market?.population_period
    ? String(market.population_period).slice(0, 4)
    : null;
  const populationIsAvailable = market?.population_status === 'available'
    && market?.population != null;
  const populationGeoLabel = market?.population_geo_level === 'county' ? ' · County proxy' : '';
  return [
    { icon: 'pi pi-map-marker', label: market?.state_name_full || market?.state_name || 'State pending' },
    { icon: 'pi pi-calendar', label: market?.month_end ? `Latest ${formatMonth(market.month_end)}` : 'Latest month pending' },
    market?.size_rank != null ? { icon: 'pi pi-chart-bar', label: `Size rank #${formatHeroInteger(market.size_rank)}` } : null,
    populationIsAvailable ? {
      icon: 'pi pi-users',
      label: `Population ${formatHeroInteger(market.population)}${populationGeoLabel}${populationYear ? ` · ACS ${populationYear}` : ''}`,
      title: market?.population_geo_level === 'county'
        ? 'ACS 5-year population estimate for the governed proxy county; this is not a real-time population count.'
        : 'ACS 5-year population estimate for the governed metro area; this is not a real-time population count.',
    } : null,
  ].filter(Boolean);
}

function toPercentValue(value) {
  if (value == null) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.abs(numeric) <= 1 ? numeric * 100 : numeric;
}

function formatHeroPercent(value, { signed = false, fractionDigits = 1 } = {}) {
  const percentValue = toPercentValue(value);
  if (percentValue == null) return '—';
  const formatted = `${percentValue.toFixed(fractionDigits)}%`;
  if (!signed || percentValue <= 0) return formatted;
  return `+${formatted}`;
}

function formatHeroYield(value) {
  if (value == null) return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return numeric.toFixed(3);
}

function formatHeroRuleRatio(value, { fractionDigits = 4 } = {}) {
  if (value == null) return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return numeric.toFixed(fractionDigits);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function marketMapUrl(market) {
  const query = [
    market?.region_name,
    market?.state_name_full || market?.state_name,
  ].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query || 'United States')}`;
}

function coerceCoordinate(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function projectLatLngToWorldPixels({ latitude, longitude }, zoom) {
  const clampedLatitude = clamp(latitude, -STATIC_MAP_MAX_LATITUDE, STATIC_MAP_MAX_LATITUDE);
  const clampedLongitude = clamp(longitude, -179.999, 179.999);
  const worldPixelSize = (2 ** zoom) * STATIC_MAP_TILE_SIZE;
  const x = ((clampedLongitude + 180) / 360) * worldPixelSize;
  const sinLatitude = Math.sin((clampedLatitude * Math.PI) / 180);
  const y = (
    0.5 - (Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI))
  ) * worldPixelSize;
  return { x, y, worldPixelSize };
}

function projectWorldPixelsToLatLng({ x, y, worldPixelSize }) {
  const wrappedX = ((x % worldPixelSize) + worldPixelSize) % worldPixelSize;
  const clampedY = clamp(y, 0, worldPixelSize);
  const longitude = ((wrappedX / worldPixelSize) * 360) - 180;
  const mercatorY = 0.5 - (clampedY / worldPixelSize);
  const latitude = (180 / Math.PI) * Math.atan(Math.sinh(mercatorY * 2 * Math.PI));
  return {
    latitude: clamp(latitude, -STATIC_MAP_MAX_LATITUDE, STATIC_MAP_MAX_LATITUDE),
    longitude: clamp(longitude, -179.999, 179.999),
  };
}

function wrapWorldPixelDelta(delta, worldPixelSize) {
  if (delta > worldPixelSize / 2) {
    return delta - worldPixelSize;
  }
  if (delta < -worldPixelSize / 2) {
    return delta + worldPixelSize;
  }
  return delta;
}

export function panMapCenter(center, deltaX, deltaY, zoom) {
  const projectedCenter = projectLatLngToWorldPixels(
    {
      latitude: center.latitude,
      longitude: center.longitude,
    },
    zoom,
  );
  return projectWorldPixelsToLatLng({
    x: projectedCenter.x - deltaX,
    y: projectedCenter.y - deltaY,
    worldPixelSize: projectedCenter.worldPixelSize,
  });
}

export function buildStaticMapMarkerStyle(marker, center, zoom) {
  const projectedMarker = projectLatLngToWorldPixels(
    {
      latitude: marker.latitude,
      longitude: marker.longitude,
    },
    zoom,
  );
  const projectedCenter = projectLatLngToWorldPixels(
    {
      latitude: center.latitude,
      longitude: center.longitude,
    },
    zoom,
  );
  const deltaX = wrapWorldPixelDelta(
    projectedMarker.x - projectedCenter.x,
    projectedCenter.worldPixelSize,
  );
  const deltaY = projectedMarker.y - projectedCenter.y;
  return {
    left: `calc(50% + ${deltaX}px)`,
    top: `calc(50% + ${deltaY}px)`,
  };
}

export function validateMarketCoordinates(market) {
  const latitude = coerceCoordinate(
    market?.latitude ?? market?.lat,
  );
  const longitude = coerceCoordinate(
    market?.longitude ?? market?.lng ?? market?.lon,
  );

  if (latitude == null || longitude == null) {
    return {
      isValid: false,
      reason: 'missing',
      latitude: null,
      longitude: null,
    };
  }

  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return {
      isValid: false,
      reason: 'invalid',
      latitude,
      longitude,
    };
  }

  return {
    isValid: true,
    reason: 'ready',
    latitude,
    longitude,
  };
}

export function buildStaticMapModel(market, frameSize = null, zoom = STATIC_MAP_DEFAULT_ZOOM) {
  const coordinateState = validateMarketCoordinates(market);
  if (!coordinateState.isValid) {
    return null;
  }

  const lat = clamp(coordinateState.latitude, -85.0511, 85.0511);
  const lng = clamp(coordinateState.longitude, -179.999, 179.999);
  const worldSize = 2 ** zoom;
  const latRad = (lat * Math.PI) / 180;
  const xFloat = worldSize * ((lng + 180) / 360);
  const yFloat = worldSize * (
    1 - (Math.log(Math.tan(latRad) + (1 / Math.cos(latRad))) / Math.PI)
  ) / 2;
  const tileX = Math.floor(xFloat);
  const tileY = Math.floor(yFloat);
  const viewportWidth = Math.max(Number(frameSize?.width) || 0, STATIC_MAP_TILE_SIZE);
  const viewportHeight = Math.max(Number(frameSize?.height) || 0, STATIC_MAP_TILE_SIZE);
  const columns = Math.min(
    STATIC_MAP_MAX_COLUMNS,
    Math.max(STATIC_MAP_GRID_SIZE, Math.ceil(viewportWidth / STATIC_MAP_TILE_SIZE) + 2),
  );
  const rows = Math.min(
    STATIC_MAP_MAX_ROWS,
    Math.max(STATIC_MAP_GRID_SIZE, Math.ceil(viewportHeight / STATIC_MAP_TILE_SIZE) + 2),
  );
  const startX = tileX - Math.floor(columns / 2);
  const startY = tileY - Math.floor(rows / 2);
  const tiles = [];

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < columns; col += 1) {
      const x = startX + col;
      const y = clamp(startY + row, 0, worldSize - 1);
      const wrappedX = ((x % worldSize) + worldSize) % worldSize;
      tiles.push({
        key: `${zoom}-${wrappedX}-${y}`,
        url: `https://tile.openstreetmap.org/${zoom}/${wrappedX}/${y}.png`,
        left: col * STATIC_MAP_TILE_SIZE,
        top: row * STATIC_MAP_TILE_SIZE,
      });
    }
  }

  const centerOffsetX = (xFloat - startX) * STATIC_MAP_TILE_SIZE;
  const centerOffsetY = (yFloat - startY) * STATIC_MAP_TILE_SIZE;
  return {
    tiles,
    layerStyle: {
      left: `calc(50% - ${centerOffsetX}px)`,
      top: `calc(50% - ${centerOffsetY}px)`,
      width: `${columns * STATIC_MAP_TILE_SIZE}px`,
      height: `${rows * STATIC_MAP_TILE_SIZE}px`,
    },
  };
}

export function MarketLocationPreview({
  market,
  stateLocatorPayload = null,
  loadingStateLocator = false,
  markers = null,
  showStateLocator = true,
  mapHref = null,
  className = '',
}) {
  const [mapTileFailed, setMapTileFailed] = useState(false);
  const [mapZoom, setMapZoom] = useState(STATIC_MAP_DEFAULT_ZOOM);
  const [isDragging, setIsDragging] = useState(false);
  const frameRef = useRef(null);
  const dragStateRef = useRef(null);
  const [frameSize, setFrameSize] = useState(null);
  const mapUrl = mapHref || marketMapUrl(market);
  const coordinateState = validateMarketCoordinates(market);
  const [viewCenter, setViewCenter] = useState(() => (
    coordinateState.isValid
      ? { latitude: coordinateState.latitude, longitude: coordinateState.longitude }
      : null
  ));
  useEffect(() => {
    const element = frameRef.current;
    if (!element) {
      return undefined;
    }

    const updateFrameSize = () => {
      const nextWidth = Math.round(element.clientWidth || 0);
      const nextHeight = Math.round(element.clientHeight || 0);
      if (!nextWidth || !nextHeight) return;
      setFrameSize((current) => (
        current?.width === nextWidth && current?.height === nextHeight
          ? current
          : { width: nextWidth, height: nextHeight }
      ));
    };

    updateFrameSize();

    // Rebuild the tile grid when the banner size changes so the selected
    // market stays centered and the static OSM layer fully covers the frame.
    window.addEventListener('resize', updateFrameSize);
    let observer = null;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(() => {
        updateFrameSize();
      });
      observer.observe(element);
    }

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateFrameSize);
    };
  }, []);

  useLayoutEffect(() => {
    setMapZoom(STATIC_MAP_DEFAULT_ZOOM);
    setMapTileFailed(false);
    setIsDragging(false);
    dragStateRef.current = null;
    setViewCenter(
      coordinateState.isValid
        ? { latitude: coordinateState.latitude, longitude: coordinateState.longitude }
        : null,
    );
  }, [
    market?.region_slug,
    market?.slug,
    coordinateState.latitude,
    coordinateState.longitude,
    coordinateState.isValid,
  ]);

  useEffect(() => {
    if (coordinateState.isValid || process.env.NODE_ENV === 'production') {
      return;
    }
    const marketIdentifier = market?.region_slug || market?.slug || market?.region_id || market?.id || 'unknown-market';
    console.warn(
      `Market map location unavailable for ${marketIdentifier}: ${coordinateState.reason} coordinates.`,
    );
  }, [
    coordinateState.isValid,
    coordinateState.reason,
    market?.id,
    market?.region_id,
    market?.region_slug,
    market?.slug,
  ]);

  const showUnavailableState = !coordinateState.isValid;
  const unavailableMessage = coordinateState.reason === 'invalid'
    ? 'Map location unavailable — coordinates invalid for this market.'
    : 'Map location unavailable — coordinates missing for this market.';
  const canZoomOut = mapZoom > STATIC_MAP_MIN_ZOOM;
  const canZoomIn = mapZoom < STATIC_MAP_MAX_ZOOM;
  const mapFrameClassName = [
    'research-market-visual__map-frame',
    !showUnavailableState && !mapTileFailed ? 'research-market-visual__map-frame--interactive' : '',
    isDragging ? 'research-market-visual__map-frame--dragging' : '',
  ].filter(Boolean).join(' ');

  const handleZoomOut = () => {
    setMapZoom((current) => Math.max(STATIC_MAP_MIN_ZOOM, current - 1));
  };

  const handleZoomIn = () => {
    setMapZoom((current) => Math.min(STATIC_MAP_MAX_ZOOM, current + 1));
  };

  const handleReset = () => {
    setMapZoom(STATIC_MAP_DEFAULT_ZOOM);
    setMapTileFailed(false);
    setIsDragging(false);
    dragStateRef.current = null;
    setViewCenter(
      coordinateState.isValid
        ? { latitude: coordinateState.latitude, longitude: coordinateState.longitude }
        : null,
    );
  };

  const handlePointerDown = (event) => {
    if (showUnavailableState || mapTileFailed || !viewCenter) {
      return;
    }
    if (event.target?.closest('.research-market-visual__map-actions')) {
      return;
    }
    if (event.pointerType !== 'touch' && event.button !== 0) {
      return;
    }
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    dragStateRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startCenter: viewCenter,
    };
    setIsDragging(true);
  };

  const handlePointerMove = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }
    event.preventDefault();
    const deltaX = event.clientX - dragState.startClientX;
    const deltaY = event.clientY - dragState.startClientY;
    setViewCenter(panMapCenter(dragState.startCenter, deltaX, deltaY, mapZoom));
  };

  const finishDrag = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }
    if (typeof event.currentTarget.releasePointerCapture === 'function') {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragStateRef.current = null;
    setIsDragging(false);
  };

  const previewMarket = viewCenter
    ? { latitude: viewCenter.latitude, longitude: viewCenter.longitude }
    : market;
  const previewMapModel = coordinateState.isValid
    ? buildStaticMapModel(previewMarket, frameSize, mapZoom)
    : null;
  const mapMarkers = markers?.length ? markers : coordinateState.isValid ? [{
    latitude: coordinateState.latitude,
    longitude: coordinateState.longitude,
    dataTestId: 'market-map-pin',
  }] : [];

  return (
    <div className={['research-market-visual', className].filter(Boolean).join(' ')} data-testid="market-map-row">
      <div className="research-market-visual__primary">
        <div
          ref={frameRef}
          className={mapFrameClassName}
          data-testid="market-map-frame"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishDrag}
          onPointerCancel={finishDrag}
          onLostPointerCapture={finishDrag}
        >
          {showUnavailableState ? (
            <div
              className="research-market-visual__map-placeholder research-market-visual__map-placeholder--error"
              role="status"
            >
              <strong>Map location unavailable</strong>
              <p>{unavailableMessage}</p>
            </div>
          ) : mapTileFailed ? (
            <div className="research-market-visual__map-placeholder" role="status">
              <strong>Static map preview unavailable</strong>
              <p>
                Map tiles did not load for this market preview. Open the external map link
                for a direct location lookup.
              </p>
            </div>
          ) : (
            <>
              <div
                className="research-market-visual__map-canvas"
                style={previewMapModel.layerStyle}
                data-testid="market-map-canvas"
                aria-hidden="true"
              >
                {previewMapModel.tiles.map((tile) => (
                  <img
                    key={tile.key}
                    className="research-market-visual__map-tile"
                    data-testid="market-map-tile"
                    src={tile.url}
                    alt=""
                    loading="lazy"
                    style={{ left: `${tile.left}px`, top: `${tile.top}px` }}
                    onError={() => setMapTileFailed(true)}
                  />
                ))}
              </div>
              {mapMarkers.map((marker, markerIndex) => {
                const latitude = Number(marker.latitude);
                const longitude = Number(marker.longitude);
                if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || !viewCenter) return null;
                return <HtmlLocationPinMarker
                  key={marker.key || marker.dataTestId || `map-marker-${markerIndex}`}
                  className={['research-market-visual__map-pin-marker', marker.className].filter(Boolean).join(' ')}
                  style={buildStaticMapMarkerStyle({ latitude, longitude }, viewCenter, mapZoom)}
                  dataTestId={marker.dataTestId || `market-map-pin-${markerIndex}`}
                  ariaHidden={marker.ariaLabel ? false : true}
                  ariaLabel={marker.ariaLabel}
                />;
              })}
              <a
                className="research-market-visual__map-attribution"
                href="https://www.openstreetmap.org/copyright"
                target="_blank"
                rel="noreferrer"
              >
                Map © OpenStreetMap
              </a>
            </>
          )}

          <div className="research-market-visual__map-actions">
            {!showUnavailableState ? (
              <div className="research-market-visual__map-control-row">
                <button
                  type="button"
                  className="research-market-visual__map-control research-market-visual__map-control--icon"
                  aria-label="Zoom map out"
                  onClick={handleZoomOut}
                  disabled={!canZoomOut}
                >
                  −
                </button>
                <button
                  type="button"
                  className="research-market-visual__map-control research-market-visual__map-control--icon"
                  aria-label="Zoom map in"
                  onClick={handleZoomIn}
                  disabled={!canZoomIn}
                >
                  +
                </button>
                <button
                  type="button"
                  className="research-market-visual__map-control research-market-visual__map-control--reset"
                  aria-label="Reset map view"
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            ) : null}

            <a
              className="research-market-visual__map-link"
              href={mapUrl}
              target="_blank"
              rel="noreferrer"
            >
              <i className="pi pi-external-link" aria-hidden="true" />
              <span>Open Map</span>
            </a>
          </div>
        </div>
      </div>

      {showStateLocator ? <div className="research-market-visual__secondary">
        <StateCountyLocatorMap
          market={market}
          countyMapPayload={stateLocatorPayload}
          loading={loadingStateLocator}
        />
      </div> : null}
    </div>
  );
}

export default function ResearchMarketDetail() {
  const navigate = useNavigate();
  const { regionSlug } = useParams();
  const [switcherMarkets, setSwitcherMarkets] = useState([]);
  const [loadingSwitcherMarkets, setLoadingSwitcherMarkets] = useState(true);
  const [switcherError, setSwitcherError] = useState('');
  const [switchingMarketSlug, setSwitchingMarketSlug] = useState(null);
  const [market, setMarket] = useState(null);
  const [loadingMarket, setLoadingMarket] = useState(true);
  const [marketError, setMarketError] = useState('');
  const [grossYieldHistory, setGrossYieldHistory] = useState(null);
  const [loadingGrossYieldHistory, setLoadingGrossYieldHistory] = useState(true);
  const [grossYieldHistoryError, setGrossYieldHistoryError] = useState('');
  const [laborSnapshot, setLaborSnapshot] = useState(null);
  const [loadingLaborSnapshot, setLoadingLaborSnapshot] = useState(true);
  const [laborSnapshotError, setLaborSnapshotError] = useState('');
  const [industryTrends, setIndustryTrends] = useState(null);
  const [loadingIndustryTrends, setLoadingIndustryTrends] = useState(true);
  const [industryTrendsError, setIndustryTrendsError] = useState('');
  const [regulationSnapshot, setRegulationSnapshot] = useState(null);
  const [loadingRegulationSnapshot, setLoadingRegulationSnapshot] = useState(true);
  const [regulationSnapshotError, setRegulationSnapshotError] = useState('');
  const [lscCountyMap, setLscCountyMap] = useState(null);
  const [loadingLscCountyMap, setLoadingLscCountyMap] = useState(true);
  const [lscCountyMapError, setLscCountyMapError] = useState('');
  const [femaRegionSnapshot, setFemaRegionSnapshot] = useState(null);
  const [loadingFemaRegionSnapshot, setLoadingFemaRegionSnapshot] = useState(true);
  const [femaRegionSnapshotError, setFemaRegionSnapshotError] = useState('');
  const [femaRegionMap, setFemaRegionMap] = useState(null);
  const [loadingFemaRegionMap, setLoadingFemaRegionMap] = useState(true);
  const [femaRegionMapError, setFemaRegionMapError] = useState('');

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    const frameId = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [regionSlug]);

  useEffect(() => {
    setSwitchingMarketSlug(null);
  }, [regionSlug]);

  useEffect(() => {
    let active = true;

    async function loadSwitcherMarkets() {
      setLoadingSwitcherMarkets(true);
      setSwitcherError('');
      try {
        const data = await apiJson('/research/markets');
        if (!active) return;
        setSwitcherMarkets(Array.isArray(data?.markets) ? data.markets : []);
      } catch (error) {
        if (!active) return;
        if (isUnauthorizedError(error)) {
          navigate('/login', { replace: true });
          return;
        }
        setSwitcherError(error?.message || 'Could not load the ranked metro list.');
      } finally {
        if (active) setLoadingSwitcherMarkets(false);
      }
    }

    void loadSwitcherMarkets();
    return () => {
      active = false;
    };
  }, [navigate]);

  useEffect(() => {
    let active = true;

    setLoadingMarket(true);
    setMarketError('');
    setMarket(null);
    setGrossYieldHistory(null);
    setLoadingGrossYieldHistory(true);
    setGrossYieldHistoryError('');
    setLaborSnapshot(null);
    setLoadingLaborSnapshot(true);
    setLaborSnapshotError('');
    setIndustryTrends(null);
    setLoadingIndustryTrends(true);
    setIndustryTrendsError('');
    setRegulationSnapshot(null);
    setLoadingRegulationSnapshot(true);
    setRegulationSnapshotError('');
    setLscCountyMap(null);
    setLoadingLscCountyMap(true);
    setLscCountyMapError('');
    setFemaRegionSnapshot(null);
    setLoadingFemaRegionSnapshot(true);
    setFemaRegionSnapshotError('');
    setFemaRegionMap(null);
    setLoadingFemaRegionMap(true);
    setFemaRegionMapError('');

    async function loadMarket() {
      try {
        const data = await apiJson(`/research/markets/${regionSlug}`);
        if (!active) return;
        setMarket(data?.market || null);
      } catch (error) {
        if (!active) return;
        if (isUnauthorizedError(error)) {
          navigate('/login', { replace: true });
          return;
        }
        setMarketError(error?.message || 'Could not load market detail.');
      } finally {
        if (active) setLoadingMarket(false);
      }
    }

    async function loadGrossYieldHistory() {
      try {
        const data = await apiJson(`/research/markets/${regionSlug}/gross-yield-history`);
        if (!active) return;
        setGrossYieldHistory(data || null);
      } catch (error) {
        if (!active) return;
        if (isUnauthorizedError(error)) {
          navigate('/login', { replace: true });
          return;
        }
        setGrossYieldHistoryError(error?.message || 'Could not load 1% rule history.');
      } finally {
        if (active) setLoadingGrossYieldHistory(false);
      }
    }

    async function loadLaborSnapshot() {
      try {
        const data = await apiJson(`/research/markets/${regionSlug}/labor-market-snapshot`);
        if (!active) return;
        setLaborSnapshot(data || null);
      } catch (error) {
        if (!active) return;
        if (isUnauthorizedError(error)) {
          navigate('/login', { replace: true });
          return;
        }
        setLaborSnapshotError(error?.message || 'Could not load labor market snapshot.');
      } finally {
        if (active) setLoadingLaborSnapshot(false);
      }
    }

    async function loadIndustryTrends() {
      try {
        const data = await apiJson(`/research/markets/${regionSlug}/industry-trends`);
        if (!active) return;
        setIndustryTrends(data || null);
      } catch (error) {
        if (!active) return;
        if (isUnauthorizedError(error)) {
          navigate('/login', { replace: true });
          return;
        }
        setIndustryTrendsError(error?.message || 'Could not load industry trends.');
      } finally {
        if (active) setLoadingIndustryTrends(false);
      }
    }

    async function loadRegulationSnapshot() {
      try {
        const data = await apiJson(`/research/markets/${regionSlug}/regulation-snapshot`);
        if (!active) return;
        setRegulationSnapshot(data || null);
      } catch (error) {
        if (!active) return;
        if (isUnauthorizedError(error)) {
          navigate('/login', { replace: true });
          return;
        }
        setRegulationSnapshotError(error?.message || 'Could not load regulation snapshot.');
      } finally {
        if (active) setLoadingRegulationSnapshot(false);
      }
    }

    async function loadLscCountyMap() {
      try {
        const data = await apiJson(`/research/markets/${regionSlug}/lsc-eviction-map`);
        if (!active) return;
        setLscCountyMap(data || null);
      } catch (error) {
        if (!active) return;
        if (isUnauthorizedError(error)) {
          navigate('/login', { replace: true });
          return;
        }
        setLscCountyMapError(error?.message || 'Could not load the LSC tracker county map.');
      } finally {
        if (active) setLoadingLscCountyMap(false);
      }
    }

    async function loadFemaRegionSnapshot() {
      try {
        const data = await apiJson(`/research/markets/${regionSlug}/fema-nri-snapshot`);
        if (!active) return;
        setFemaRegionSnapshot(data || null);
      } catch (error) {
        if (!active) return;
        if (isUnauthorizedError(error)) {
          navigate('/login', { replace: true });
          return;
        }
        setFemaRegionSnapshotError(error?.message || 'Could not load FEMA region evidence.');
      } finally {
        if (active) setLoadingFemaRegionSnapshot(false);
      }
    }

    async function loadFemaRegionMap() {
      try {
        const data = await apiJson(`/research/markets/${regionSlug}/fema-nri-map?hazard=overall`);
        if (!active) return;
        setFemaRegionMap(data || null);
      } catch (error) {
        if (!active) return;
        if (isUnauthorizedError(error)) {
          navigate('/login', { replace: true });
          return;
        }
        setFemaRegionMapError(error?.message || 'Could not load FEMA region map.');
      } finally {
        if (active) setLoadingFemaRegionMap(false);
      }
    }

    void loadMarket();
    void loadGrossYieldHistory();
    void loadLaborSnapshot();
    void loadIndustryTrends();
    void loadRegulationSnapshot();
    void loadLscCountyMap();
    void loadFemaRegionSnapshot();
    void loadFemaRegionMap();
    return () => {
      active = false;
    };
  }, [navigate, regionSlug]);

  if (loadingMarket) {
    return (
      <div className="research-page">
        <PageLoader label="Loading market details" />
      </div>
    );
  }

  const regionName = market?.region_name || 'Market';
  const conciseRegionName = String(regionName).split(',')[0].trim() || 'Market';
  const rankedMarkets = sortResearchMarkets(switcherMarkets, DEFAULT_RESEARCH_MARKET_SORT);
  const rankedActiveMarket = findResearchMarketBySlug(rankedMarkets, regionSlug);
  const currentRankIndex = rankedMarkets.findIndex((candidate) => candidate?.region_slug === regionSlug);
  const immediateRank = Number(market?.current_rank);
  const currentRankLabel = Number.isFinite(immediateRank) && immediateRank > 0
    ? `#${immediateRank}`
    : currentRankIndex >= 0 ? `#${currentRankIndex + 1}` : '—';
  const currentRuleLabel = formatResearchMarketRulePercent(
    getResearchMarketLatestRuleRatio(rankedActiveMarket || market),
  );
  const marketHeroTitleAside = (
    <div className="research-market-hero-rank" data-testid="market-hero-rank">
      <span className="research-market-hero-rank__label">
        {switchingMarketSlug ? 'Switching market…' : 'Current rank'}
      </span>
      <strong className="research-market-hero-rank__value">
        <span className="research-market-hero-rank__number">{currentRankLabel}</span>
        <span className="research-market-hero-rank__separator" aria-hidden="true">|</span>
        <span className="research-market-hero-rank__number">{currentRuleLabel}</span>
      </strong>
    </div>
  );

  function handleSwitchMarket(nextSlug) {
    if (!nextSlug || nextSlug === regionSlug) return;
    setSwitchingMarketSlug(nextSlug);
    startTransition(() => {
      navigate(`/research/markets/${nextSlug}`);
    });
  }

  const marketHeroMetaItems = buildMarketHeroMetaItems(market);
  const marketHeroStats = market ? [
    [
      {
        label: 'Home $',
        value: formatHeroCurrency(market.home_prices),
        sub: market.home_yoy_pct == null ? 'Home YoY pending' : `Home YoY ${formatHeroPercent(market.home_yoy_pct, { signed: true })}`,
        title: formatHeroCurrency(market.home_prices),
      },
      {
        label: 'Rent $',
        value: formatHeroCurrency(market.rent_prices),
        sub: 'Latest Zillow rent estimate',
        title: `${formatHeroCurrency(market.rent_prices)} • Zillow metro rent`,
      },
      {
        label: '1% Rule',
        value: formatHeroRuleRatio(market.rent_to_price_ratio_latest),
        sub: market.rent_to_price_ratio_latest == null
          ? 'Latest monthly rent-to-price ratio pending'
          : `Latest Zillow rent-to-price ratio (${formatHeroPercent(market.rent_to_price_ratio_latest)})`,
        title: market.rent_to_price_ratio_latest == null
          ? '—'
          : `${formatHeroRuleRatio(market.rent_to_price_ratio_latest, { fractionDigits: 4 })} (${formatHeroPercent(market.rent_to_price_ratio_latest, { fractionDigits: 2 })}) • latest Zillow reading`,
      },
      {
        label: 'Gross Yield',
        value: formatHeroYield(market.gross_yield_latest),
        sub: market.gross_yield_latest == null
          ? 'Latest monthly annual gross yield pending'
          : 'Latest Zillow monthly annual gross yield',
        title: market.gross_yield_latest == null
          ? '—'
          : `${formatHeroYield(market.gross_yield_latest)} • latest Zillow reading`,
      },
    ],
  ] : [];

  return (
    <div className="research-page">
      <div className="research-shell">
        <ResearchMarketSwitcherSection
          markets={switcherMarkets}
          activeRegionSlug={regionSlug}
          loading={loadingSwitcherMarkets}
          error={switcherError}
          switching={Boolean(switchingMarketSlug)}
          onSelectSlug={handleSwitchMarket}
        />

        {marketError ? (
          <section className="research-detail-hero">
            <div className="research-detail-hero__head">
              <div>
                <Link className="research-detail-back" to="/research">
                  <i className="pi pi-arrow-left" />
                  Back to Market Research
                </Link>
                <h1 className="research-detail-title">{regionName}</h1>
                <p className="research-detail-subtitle">Market Research Detail Page</p>
              </div>
            </div>
            <div className="research-detail-state research-detail-state--error">{marketError}</div>
          </section>
        ) : (
          <DetailHeroCard
            backTo="/research"
            backLabel="Back to Market Research"
            backPlacement="title"
            title={regionName}
            titleAside={marketHeroTitleAside}
            // subtitle={`US Winners metro snapshot for ${market?.state_name_full || market?.state_name || 'the selected market'}. This top card uses the same region-level Zillow dashboard row as the main Market Research table.`}
            metaItems={marketHeroMetaItems}
            statsRows={marketHeroStats}
            className="detail-hero-card--market-map"
            visual={(
              <MarketLocationPreview
                market={market}
                stateLocatorPayload={lscCountyMap}
                loadingStateLocator={loadingLscCountyMap}
              />
            )}
          />
        )}

        <ZillowTabbedTimelineDemo
          loading={loadingGrossYieldHistory}
          error={grossYieldHistoryError}
          payload={grossYieldHistory}
        />

        <LaborTabbedChartDemo
          loading={loadingLaborSnapshot}
          error={laborSnapshotError}
          snapshot={laborSnapshot}
        />

        <IndustryTrendPrototype
          loading={loadingIndustryTrends}
          error={industryTrendsError}
          payload={industryTrends}
        />

        <FemaRegionPrototypeSection
          loading={loadingFemaRegionSnapshot}
          error={femaRegionSnapshotError}
          payload={femaRegionSnapshot}
        />

        <FemaNriRegionMapSection
          initialPayload={femaRegionMap}
          loading={loadingFemaRegionMap}
          error={femaRegionMapError}
          market={market}
        />



        {/* Original stacked Zillow charts retained for possible restoration after prototype review.
        <GrossYieldTimelineSection
          loading={loadingGrossYieldHistory}
          error={grossYieldHistoryError}
          payload={grossYieldHistory}
        />
        */}

        {/* Original stacked Labor Market Snapshot charts retained for possible restoration after prototype review.
        <LaborMarketSnapshotSection
          loading={loadingLaborSnapshot}
          error={laborSnapshotError}
          snapshot={laborSnapshot}
        />
        */}

        <Suspense fallback={<PageLoader compact label="Loading eviction map" />}>
          <LscEvictionTrackerMapSection
            loading={loadingLscCountyMap}
            error={lscCountyMapError}
            payload={lscCountyMap}
            showSourceAudit
            title={`LSC Eviction Tracker | ${(market?.state_name_full || market?.state_name || lscCountyMap?.state?.name || 'State')} County Heat Map`}
            subtitle={lscCountyMap?.has_market_county_overlap === false
              ? `${conciseRegionName}’s counties are not covered. This map provides broader ${(market?.state_name_full || market?.state_name || lscCountyMap?.state?.name || 'state')} context only.`
              : `County-level monthly eviction tracker context for ${regionName}. The selected market's tracked counties stay outlined so you can compare metro coverage against the broader ${(market?.state_name_full || market?.state_name || lscCountyMap?.state?.name || 'state')} pattern.`}
          />
        </Suspense>

        <LandlordRegulationSnapshotSection
          loading={loadingRegulationSnapshot}
          error={regulationSnapshotError}
          payload={regulationSnapshot}
        />

        {/* <section className="research-detail-grid">
          {PLACEHOLDER_SECTIONS.map((title) => (
            <article key={title} className="research-detail-card">
              <h2>{title}</h2>
              <p>
                Placeholder section for future market research integrations. This route now preserves the selected metro identity and is ready for deeper underwriting, local research, and narrative reporting.
              </p>
            </article>
          ))}
        </section> */}
      </div>
    </div>
  );
}
